import { NavigationGuardNext, RouteLocationNormalized } from "vue-router";
import CertificationService from "@/core/services/CertificationService";
import { getCertificationRouteName } from "@/core/helpers/certification-routes";
import Swal from "sweetalert2/dist/sweetalert2.js";

/**
 * Guard para validar acesso às rotas de certificação
 * Verifica o status da certificação e redireciona para a rota correta
 */
export async function certificationGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  const certificationId = to.params.id as string;

  // Se não há ID, permite navegar normalmente (caso da index)
  if (!certificationId) {
    return next();
  }

  try {
    // 1. Busca o status da certificação
    const { data: statusData, status } = await CertificationService.getStatus(certificationId);

    if (status === 200) {
      const currentStep = statusData.data.current_step;
      
      // 2. Busca a configuração da certificação
      const { data: configData } = await CertificationService.getConfig(certificationId);

      // 3. Encontra o step correto baseado no order do array de steps
      const currentStepConfig = configData.data.steps.find((step: any) => step.order === currentStep);
      
      // Se não encontrou o step ou o componente, permite continuar
      if (!currentStepConfig || !currentStepConfig.component) {
        return next();
      }

      // 4. Determina qual componente o usuário deveria estar vendo
      const expectedComponent = currentStepConfig.component;
      const expectedRouteName = getCertificationRouteName(expectedComponent);

      // Se não encontrou rota válida, redireciona para index
      if (!expectedRouteName) {
        return next({
            name: 'certifications',
            replace: true
        });
      }

      // 5. Se o usuário já está na rota correta, permite continuar
      if (to.name === expectedRouteName) {
        return next();
      }

      // 5.1. Reprovado com cooldown encerrado: libera a página inicial para iniciar nova tentativa
      if (statusData.data.can_retry === true && to.name === 'certifications-detail') {
        return next();
      }

      // 6. Caso contrário, redireciona para a rota correta
      return next({
        name: expectedRouteName,
        params: { id: certificationId },
        replace: true
      });
    }

    // Se status não é 200, redireciona para index
    return next({
        name: 'certifications',
        replace: true
    });;

  } catch (error: any) {
    if(to.name == 'certifications-detail'){
      return next();
    }
    
    // Se retornou erro 403, significa que a certificação não foi iniciada
    if (error?.response?.status === 403) {
      // Exibe mensagem de aviso
      await Swal.fire({
        icon: 'warning',
        title: 'Aviso',
        text: 'Certificado ainda não iniciado',
        confirmButtonText: 'OK'
      });

      // Redireciona para a index de certificações
      return next({
        name: 'certifications',
        replace: true
      });
    }

    // Para outros erros, redireciona para index
    return next({
        name: 'certifications',
        replace: true
    });;
  }
}
